/**
 * Currency utilities for formatting monetary values based on user's country
 */

// Country code to currency code mapping
export const COUNTRY_TO_CURRENCY = {
  // North America
  'US': 'USD',
  'USA': 'USD',
  'United States': 'USD',
  'CA': 'CAD',
  'Canada': 'CAD',
  'MX': 'MXN',
  'Mexico': 'MXN',
  
  // South Asia
  'IN': 'INR',
  'India': 'INR',
  'PK': 'PKR',
  'Pakistan': 'PKR',
  'BD': 'BDT',
  'Bangladesh': 'BDT',
  'LK': 'LKR',
  'Sri Lanka': 'LKR',
  'NP': 'NPR',
  'Nepal': 'NPR',
  
  // Europe
  'GB': 'GBP',
  'UK': 'GBP',
  'United Kingdom': 'GBP',
  'DE': 'EUR',
  'Germany': 'EUR',
  'FR': 'EUR',
  'France': 'EUR',
  'IT': 'EUR',
  'Italy': 'EUR',
  'ES': 'EUR',
  'Spain': 'EUR',
  'CH': 'CHF',
  'Switzerland': 'CHF',
  
  // East Asia
  'CN': 'CNY',
  'China': 'CNY',
  'JP': 'JPY',
  'Japan': 'JPY',
  'KR': 'KRW',
  'South Korea': 'KRW',
  'HK': 'HKD',
  'Hong Kong': 'HKD',
  'SG': 'SGD',
  'Singapore': 'SGD',
  
  // Southeast Asia
  'TH': 'THB',
  'Thailand': 'THB',
  'MY': 'MYR',
  'Malaysia': 'MYR',
  'ID': 'IDR',
  'Indonesia': 'IDR',
  'PH': 'PHP',
  'Philippines': 'PHP',
  'VN': 'VND',
  'Vietnam': 'VND',
  
  // Middle East
  'AE': 'AED',
  'UAE': 'AED',
  'United Arab Emirates': 'AED',
  'SA': 'SAR',
  'Saudi Arabia': 'SAR',
  'IL': 'ILS',
  'Israel': 'ILS',
  
  // Oceania
  'AU': 'AUD',
  'Australia': 'AUD',
  'NZ': 'NZD',
  'New Zealand': 'NZD',
  
  // Africa
  'ZA': 'ZAR',
  'South Africa': 'ZAR',
  'NG': 'NGN',
  'Nigeria': 'NGN',
  'KE': 'KES',
  'Kenya': 'KES',
  'EG': 'EGP',
  'Egypt': 'EGP',
  
  // South America
  'BR': 'BRL',
  'Brazil': 'BRL',
  'AR': 'ARS',
  'Argentina': 'ARS',
  'CL': 'CLP',
  'Chile': 'CLP',
  'CO': 'COP',
  'Colombia': 'COP',
};

// Currency symbols
export const CURRENCY_SYMBOLS = {
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'INR': '₹',
  'JPY': '¥',
  'CNY': '¥',
  'CAD': 'C$',
  'AUD': 'A$',
  'NZD': 'NZ$',
  'CHF': 'CHF',
  'SEK': 'kr',
  'NOK': 'kr',
  'DKK': 'kr',
  'PLN': 'zł',
  'RUB': '₽',
  'BRL': 'R$',
  'MXN': 'MX$',
  'ZAR': 'R',
  'SGD': 'S$',
  'HKD': 'HK$',
  'KRW': '₩',
  'THB': '฿',
  'MYR': 'RM',
  'IDR': 'Rp',
  'PHP': '₱',
  'VND': '₫',
  'AED': 'د.إ',
  'SAR': '﷼',
  'ILS': '₪',
  'NGN': '₦',
  'KES': 'KSh',
  'EGP': 'E£',
  'PKR': '₨',
  'BDT': '৳',
  'LKR': 'Rs',
  'NPR': 'रू',
  'ARS': '$',
  'CLP': '$',
  'COP': '$',
};

/**
 * Get currency code from country
 * @param {string} country - Country name or code
 * @returns {string} Currency code (defaults to USD)
 */
export const getCurrencyFromCountry = (country) => {
  if (!country) return 'USD';
  
  // Try exact match first
  if (COUNTRY_TO_CURRENCY[country]) {
    return COUNTRY_TO_CURRENCY[country];
  }
  
  // Try case-insensitive match
  const countryUpper = country.toUpperCase();
  const countryLower = country.toLowerCase();
  
  for (const [key, value] of Object.entries(COUNTRY_TO_CURRENCY)) {
    if (key.toUpperCase() === countryUpper || key.toLowerCase() === countryLower) {
      return value;
    }
  }
  
  // Default to USD
  return 'USD';
};

/**
 * Get currency symbol from currency code
 * @param {string} currencyCode - Currency code (e.g., 'USD', 'INR')
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode) => {
  return CURRENCY_SYMBOLS[currencyCode] || currencyCode;
};

/**
 * Format amount with currency
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code (e.g., 'USD', 'INR')
 * @param {object} options - Intl.NumberFormat options
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currencyCode = 'USD', options = {}) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    amount = 0;
  }
  
  try {
    return new Intl.NumberFormat(currencyCode === 'INR' ? 'en-IN' : 'en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      ...options,
    }).format(amount);
  } catch (error) {
    // Fallback if currency code is invalid
    const symbol = getCurrencySymbol(currencyCode);
    return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  }
};

/**
 * Format amount with just the symbol (for backward compatibility)
 * @param {number} amount - Amount to format
 * @param {string} currencyCode - Currency code
 * @returns {string} Formatted string with symbol
 */
export const formatWithSymbol = (amount, currencyCode = 'USD') => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    amount = 0;
  }
  
  const symbol = getCurrencySymbol(currencyCode);
  const formattedAmount = amount.toLocaleString('en-US', { 
    minimumFractionDigits: 0,
    maximumFractionDigits: 2 
  });
  
  return `${symbol}${formattedAmount}`;
};
